import { BreakpointObserver } from "@angular/cdk/layout";
import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FX_MEDIA_QUERIES, FxBreakpoint, pickResponsiveValue, toBoolean } from "./breakpoints";

@Directive({
  standalone: false,
  selector: "[fxShow]"
})
export class FxShowDirective implements OnInit, OnChanges, OnDestroy {
  @Input() fxShow?: unknown;
  @Input("fxShow.xs") fxShowXs?: unknown;
  @Input("fxShow.sm") fxShowSm?: unknown;
  @Input("fxShow.md") fxShowMd?: unknown;
  @Input("fxShow.gt-xs") fxShowGtXs?: unknown;
  @Input("fxShow.gt-sm") fxShowGtSm?: unknown;
  @Input("fxShow.gt-md") fxShowGtMd?: unknown;

  private readonly destroy$ = new Subject<void>();
  private readonly activeBreakpoints = new Set<FxBreakpoint>();

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.breakpointObserver
      .observe(Object.values(FX_MEDIA_QUERIES))
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.activeBreakpoints.clear();
        (Object.keys(FX_MEDIA_QUERIES) as FxBreakpoint[]).forEach((breakpoint) => {
          if (state.breakpoints[FX_MEDIA_QUERIES[breakpoint]]) {
            this.activeBreakpoints.add(breakpoint);
          }
        });
        this.applyVisibility();
      });

    this.applyVisibility();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.applyVisibility();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyVisibility(): void {
    const value = pickResponsiveValue(
      this.fxShow,
      {
        "xs": this.fxShowXs,
        "sm": this.fxShowSm,
        "md": this.fxShowMd,
        "gt-xs": this.fxShowGtXs,
        "gt-sm": this.fxShowGtSm,
        "gt-md": this.fxShowGtMd
      },
      (breakpoint) => this.activeBreakpoints.has(breakpoint)
    );

    const isVisible = toBoolean(value, true);
    if (isVisible) {
      this.renderer.removeStyle(this.host.nativeElement, "display");
      return;
    }

    this.renderer.setStyle(this.host.nativeElement, "display", "none");
  }
}

@Directive({
  standalone: false,
  selector: "[fxHide]"
})
export class FxHideDirective implements OnInit, OnChanges, OnDestroy {
  @Input() fxHide?: unknown;
  @Input("fxHide.xs") fxHideXs?: unknown;
  @Input("fxHide.sm") fxHideSm?: unknown;
  @Input("fxHide.md") fxHideMd?: unknown;
  @Input("fxHide.gt-xs") fxHideGtXs?: unknown;
  @Input("fxHide.gt-sm") fxHideGtSm?: unknown;
  @Input("fxHide.gt-md") fxHideGtMd?: unknown;

  private readonly destroy$ = new Subject<void>();
  private readonly activeBreakpoints = new Set<FxBreakpoint>();

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.breakpointObserver
      .observe(Object.values(FX_MEDIA_QUERIES))
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.activeBreakpoints.clear();
        (Object.keys(FX_MEDIA_QUERIES) as FxBreakpoint[]).forEach((breakpoint) => {
          if (state.breakpoints[FX_MEDIA_QUERIES[breakpoint]]) {
            this.activeBreakpoints.add(breakpoint);
          }
        });
        this.applyVisibility();
      });

    this.applyVisibility();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.applyVisibility();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyVisibility(): void {
    const value = pickResponsiveValue(
      this.fxHide,
      {
        "xs": this.fxHideXs,
        "sm": this.fxHideSm,
        "md": this.fxHideMd,
        "gt-xs": this.fxHideGtXs,
        "gt-sm": this.fxHideGtSm,
        "gt-md": this.fxHideGtMd
      },
      (breakpoint) => this.activeBreakpoints.has(breakpoint)
    );

    const shouldHide = toBoolean(value, true);
    if (shouldHide) {
      this.renderer.setStyle(this.host.nativeElement, "display", "none");
      return;
    }

    this.renderer.removeStyle(this.host.nativeElement, "display");
  }
}
